/** @format */
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { isSafeInteger } from 'lodash-es';
import { CartWizardStepHeadingComponent } from './cart-wizard-step-heading/cart-wizard-step-heading.component';

/**
 * Add a {@link CartWizardStepHeadingComponent} inside this component to show the selected wizard step's heading.
 * When moving between steps _inside_ the wizard (not when entering it), autofocus the {@link CartWizardStepHeadingComponent} to help
 * screen readers start from the essential, _new_ information.
 * Autofocus must not be done when entering the wizard, because we want to give the user a chance to see the context first.
 */
@Component({
    selector: 'app-cart-wizard-container',
    templateUrl: './cart-wizard-container.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CartWizardContainerComponent implements OnInit, OnDestroy {
    protected readonly pageTitleKey: string = 'OPEN_UNIVERSITY_CART.CART';

    private readonly _appNameKey: string = 'PAGE_TITLE_STUDENT';
    private _wizardStepKeys: readonly string[];
    private _currentStep: number;
    private _initialized: boolean = false;

    constructor(
        private readonly _title: Title,
        private readonly _translocoService: TranslocoService,
    ) {}

    ngOnInit(): void {
        this._initialized = true;
        this.updateTitle();
    }

    ngOnDestroy(): void {
        this._title.setTitle(this._translocoService.translate(this._appNameKey));
    }

    /**
     * Translation keys of the wizard steps to show.
     */
    @Input({ required: true })
    set wizardStepKeys(value: readonly string[]) {
        this._wizardStepKeys = value;
        this.updateTitle();
    }

    get wizardStepKeys(): readonly string[] {
        return this._wizardStepKeys;
    }

    /**
     * Zero-based index of the selected step in {@link wizardStepKeys}.
     */
    @Input({ required: true }) set currentStep(currentStep: number) {
        this._currentStep = currentStep;
        this.updateTitle();
    }

    get currentStep(): number {
        if (
            !this.wizardStepKeys ||
            !isSafeInteger(this._currentStep) ||
            this._currentStep < 0 ||
            this._currentStep >= this.wizardStepKeys.length
        ) {
            return 0;
        }

        return this._currentStep;
    }

    private updateTitle(): void {
        if (!this._initialized) {
            return;
        }

        if (!this._wizardStepKeys?.length) {
            return;
        }

        const pageTitle: string = this._translocoService.translate(this.pageTitleKey);

        const wizardStepKey: string = this._wizardStepKeys[this.currentStep];
        const wizardStep: string = this._translocoService.translate(wizardStepKey);

        const appName: string = this._translocoService.translate(this._appNameKey);

        this._title.setTitle(`${pageTitle}, ${wizardStep} - ${appName}`);
    }
}
